import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import LogicFlow from '@logicflow/core';
import '@logicflow/core/es/index.css';
import '@logicflow/extension/dist/index.css';
import FloatingToolbar from './components/FloatingToolbar';
import useDataMapControl from './hooks/useDataMapControl';
import { useDataMapFileOperations } from './hooks/useDataMapFileOperations';
import DataMapPanel from './components/DataMapPanel';
import useDataMapNodeOperations from './hooks/useDataMapOperaations';
import { useInitDataMap } from './hooks/useInitDataMap';
import AddRelationshipModal from './components/modals/AddRelationshipModal';
import useLogicFlowInitializer from './hooks/useLogicFlowInitializer';
import useRelationshipModal, { RelationshipDataType } from './hooks/useRelationshipModal';
import useDragAndDrop from './hooks/useDragAndDrop';
import TopLeftPanel from './components/TopLeftPanel';
import CanvasDeleteButtons from './components/CanvasDeleteButtons';
import { useDeleteButton } from './hooks/useDeleteButton';
import './style/index.css';

const DataMapEditor: React.FC = memo(() => {
  // 画布相关ref
  const canvasRef = useRef<HTMLDivElement>(null);
  const lfRef = useRef<LogicFlow | null>(null);
  
  // 搜索相关状态
  const [savedViewState, setSavedViewState] = useState<any>(null);
  
  // 初始请求钩子
  const { mapViewsId, systems, graphData, setSystems, getDataSources } = useInitDataMap();
  
  // 处理系统变更
  const handleSystemChange = useCallback((systems: any[]) => {
    // 这里可以添加系统变更的处理逻辑
    console.log('系统变更:', systems);
  }, []);

  // 使用拖拽和放置hook，mapViewsId作为依赖项
  const { handleTableDragStart, handleCanvasDrop, handleCanvasDragOver } = useDragAndDrop({
    canvasRef,
    lfRef,
    mapViewsId
  });

  // 地图控制相关hooks
  const {
    handleZoomIn,
    handleZoomOut,
    handleResetView,
    handleSaveViewState,
    handleRestoreViewState,
  } = useDataMapControl({
    lfRef,
    canvasRef,
    setSavedViewState,
    savedViewState,
  });

  // 文件操作相关 hooks
  const {
    handleSave,
    handleExportJSON,
    handleExportImage,
    handleExportGraphData,
    handleImportJSON,
  } = useDataMapFileOperations({
    handleResetView,
    lfRef,
  });

  // 节点操作相关 hooks
  const {
    selectedNodeId,
    selectedNodeIdRef,
    nodeMenuPosition,
    showNodeMenu,
    showMenuDropdown,
    handleNodeClick,
    toggleMenuDropdown,
    hideNodeMenu,
    handleDeleteNode,
  } = useDataMapNodeOperations({ lfRef });

  // 选中的边线ID
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  // 选中的边线ID引用，用于在闭包中获取最新值
  const selectedEdgeIdRef = useRef<string | null>(null);

  // 同步 selectedEdgeId 到 ref
  useEffect(() => {
    selectedEdgeIdRef.current = selectedEdgeId;
  }, [selectedEdgeId]);

  // 使用关联弹窗hook
  const { 
    relationshipModalVisible, 
    relationshipData, 
    handleRelationshipConfirm, 
    handleRelationshipCancel,
    openRelationshipModal,
    selectedRelationship
  } = useRelationshipModal({ lfRef, mapViewsId });

  // 处理关联弹窗打开
  const handleRelationshipModalOpen = useCallback((data: RelationshipDataType, edgeId: string | null) => {
    try {
      console.log('handleRelationshipModalOpen', data, edgeId);
      if (lfRef.current && edgeId) {
        // 手动连线表与表时，先删除当前添加的边线
        lfRef.current.deleteEdge(edgeId);
        // 打开关联弹窗
        openRelationshipModal({
          leftTable: data.leftTable,
          rightTable: data.rightTable,
          sourceAnchorId: data.sourceAnchorId,
          targetAnchorId: data.targetAnchorId,
          conditions: data.conditions
        }, edgeId);
      }
    } catch (error) {
      console.error('打开关联弹窗失败:', error);
    }
  }, [lfRef, openRelationshipModal]);

  // 使用LogicFlow初始化hook
  const { deleteEdgeWithApi, deleteNodeWithApi } = useLogicFlowInitializer({
    mapViewsId,
    canvasRef,
    lfRef,
    initialData: graphData,
    onNodeClick: handleNodeClick,
    onRelationshipModalOpen: handleRelationshipModalOpen,
    selectedNodeIdRef,
    selectedEdgeIdRef,
    hideNodeMenu,
    setSelectedEdgeId,
  });

  // 使用删除按钮 Hook
  const {
    edgeDeleteBtnPos,
    nodeDeleteBtnPos,
    handleEdgeDeleteBtnClick,
    handleNodeDeleteBtnClick,
  } = useDeleteButton({
    lfRef,
    canvasRef,
    selectedEdgeId,
    selectedNodeId,
    selectedEdgeIdRef,
    selectedNodeIdRef,
    deleteEdgeWithApi,
    deleteNodeWithApi,
    setSelectedEdgeId,
  });

  return (
    <div className={`editorBox`}>
      {/* 左侧节点面板  */}
      <div className="ontology-sidebar w-[320px]">
        <DataMapPanel 
          onSystemChange={handleSystemChange} 
          systems={systems} 
          setSystems={setSystems} 
          onTableDragStart={handleTableDragStart}
          getDataSources={getDataSources}
          mapViewsId={mapViewsId}
        />
      </div>
      
      {/* 画布 */}
      <div 
        className="app-content" 
        ref={canvasRef}
        onDrop={handleCanvasDrop}
        onDragOver={handleCanvasDragOver}
      />

      {/* 右侧悬浮工具栏 */}
      <FloatingToolbar
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetView={handleResetView}
        onSave={handleSave}
        onExportJSON={handleExportJSON}
        onExportImage={handleExportImage}
        onImportJSON={handleImportJSON}
      />
      
      {/* 左上角筛选组件 */}
      <TopLeftPanel
        onSaveViewState={handleSaveViewState}
        onRestoreViewState={handleRestoreViewState}
        onResetView={handleResetView}
      />
      
      {/* 删除按钮 */}
      <CanvasDeleteButtons
        edgeDeleteBtnPos={edgeDeleteBtnPos}
        nodeDeleteBtnPos={nodeDeleteBtnPos}
        onEdgeDelete={handleEdgeDeleteBtnClick}
        onNodeDelete={handleNodeDeleteBtnClick}
        canvasRef={canvasRef}
      />
      
      {/* 关联类型弹窗 */}
      <AddRelationshipModal
        visible={relationshipModalVisible}
        onClose={handleRelationshipCancel}
        onConfirm={handleRelationshipConfirm}
        leftTable={relationshipData.leftTable}
        rightTable={relationshipData.rightTable}
        conditions={relationshipData.conditions}
      />
    </div>
  );
});

DataMapEditor.displayName = 'DataMapEditor';

export default DataMapEditor;
